/* global turf */
import { MapConstants, surfaceTypes } from '../constants/constants';
import { getLines, getPoints } from '../helpers/collectionHelper';

export const initialiseAdminMapListeners = (
    mapInstance,
    sid,
    lid,
    token,
    draw,
    onClick,
) => {
    mapInstance.on('load', () => {
        // Sources
        addInitialSources(mapInstance, sid, token);
        addFocusSources(mapInstance);
        addDrawRouteSources(mapInstance);
        // Layers
        addBaseLayers(mapInstance, lid);
        addFocusLayers(mapInstance);
        addDataLayers(mapInstance);
        addDrawRoutesLayers(mapInstance);
    });
    mapInstance.on('lineSlice', (p) => {
        const collection = draw.getAll();
        const lines = getLines(collection);
        const points = getPoints(collection);
        const newLines = lines.map((line) => {
            const fromPoint = turf.point(line.geometry.coordinates[0]);
            const toPoint = turf.point([p.position.lng, p.position.lat]);
            const sliced = turf.lineSlice(fromPoint, toPoint, line);
            sliced.properties = { ...line.properties };
            return sliced;
        });
        const newCollection = turf.featureCollection([...newLines, ...points]);
        draw.set(newCollection);
    });
    mapInstance.on('click', (e) => {
        const { lng, lat } = e.lngLat;
        onClick([lng, lat]);
    });
};

export const initialiseClientListeners = (
    mapInstance,
    sid,
    lid,
    token,
    mapLoaded,
    onTrailClicked,
) => {
    mapInstance.on('resize', () => {
        console.log('resize');
    });
    mapInstance.on('click', 'preview-geojson-symbol-home-points', (e) => {
        console.log(e.features[0].properties);
        onTrailClicked(e.features[0].properties.trailUUID);
    });
    mapInstance.on('load', () => {
        // Sources
        addInitialSources(mapInstance, sid, token);
        addFocusSources(mapInstance);
        addDrawRouteSources(mapInstance);
        addTerrainSource(mapInstance);
        // Layers
        addBaseLayers(mapInstance, lid);
        addFocusLayers(mapInstance);
        addDataLayers(mapInstance);
        addAdditionalLayers(mapInstance, true);
        mapLoaded();
    });
};

const addInitialSources = (map, sid, token) => {
    map.addSource(MapConstants.id.source.geojsonPreview, {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: [],
        },
    });
    map.addSource(MapConstants.id.source.currentPositionMarker, {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: [],
        },
    });
    map.addSource(MapConstants.id.source.satellite, {
        type: 'raster',
        url: 'mapbox://mapbox.satellite',
        tileSize: 256,
    });
    map.addSource(MapConstants.id.source.rgbTerrain, {
        type: 'raster',
        tiles: [
            `https://api.mapbox.com/v4/mapbox.terrain-rgb/{z}/{x}/{y}.pngraw?access_token=${token}`,
        ],
        tileSize: 256,
    });
    map.addSource(MapConstants.id.source.mines, {
        type: 'vector',
        url: `mapbox://${sid}`,
    });
};

const addTerrainSource = (map) => {
    map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14,
    });
    map.setTerrain({
        source: 'mapbox-dem',
        exaggeration: 1.5,
    });
};

const addDrawRouteSources = (map) => {
    map.addSource(MapConstants.id.source.geojsonRoutePreview, {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: [],
        },
    });
};

const addFocusSources = (map) => {
    map.addSource(MapConstants.id.source.geojsonFocus, {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: [],
        },
    });
};

const addBaseLayers = (map, lid) => {
    // Add satellite layer
    map.addLayer(
        {
            id: MapConstants.id.layer.satellite,
            type: 'raster',
            source: MapConstants.id.source.satellite,
            paint: {
                'raster-saturation': 0.5,
            },
            layout: {
                visibility: 'none',
            },
        },
        MapConstants.placeHolderLayerId,
    );
    // Add mines layer
    map.addLayer(
        {
            id: MapConstants.id.layer.mines,
            type: 'fill',
            source: MapConstants.id.source.mines,
            'source-layer': lid,
            paint: {
                'fill-color': MapConstants.style.minesRed,
                'fill-opacity': 0.3,
            },
            layout: {
                visibility: 'visible',
            },
            minzoom: 10,
        },
        MapConstants.placeHolderLayerId,
    );
};

const addDataLayers = (map) => {
    // Outline 1
    map.addLayer(
        {
            id: `${MapConstants.id.layer.geojsonPreview.line}-white-outline`,
            type: 'line',
            source: MapConstants.id.source.geojsonPreview,
            paint: {
                'line-color': '#ffffff',
                'line-opacity': 0.5,
                'line-width': MapConstants.style.line.width.outlineWide,
            },
            layout: {
                visibility: 'visible',
            },
            filter: ['all', ['!has', 'isHome'], ['==', '$type', 'LineString']],
        },
        MapConstants.placeHolderLayerId,
    );
    //Outline 2
    map.addLayer(
        {
            id: `${MapConstants.id.layer.geojsonPreview.line}-black-outline`,
            type: 'line',
            source: MapConstants.id.source.geojsonPreview,
            paint: {
                // 'line-color': '#ffffff',
                'line-color': '#000000',
                'line-opacity': 0.8,
                'line-width': MapConstants.style.line.width.outlineNormal,
            },
            layout: {
                visibility: 'visible',
            },
            filter: ['all', ['!has', 'isHome'], ['==', '$type', 'LineString']],
        },
        MapConstants.placeHolderLayerId,
    );
    // Under line
    map.addLayer(
        {
            id: `${MapConstants.id.layer.geojsonPreview.line}-white-underline`,
            type: 'line',
            source: MapConstants.id.source.geojsonPreview,
            paint: {
                'line-color': '#ffffff',
                'line-opacity': 1,
                'line-width': MapConstants.style.line.width.dataLineString,
            },
            layout: {
                visibility: 'visible',
            },
            filter: ['all', ['!has', 'isHome'], ['==', '$type', 'LineString']],
        },
        MapConstants.placeHolderLayerId,
    );
    // Add geojson lines
    surfaceTypes.forEach((surfaceType) => {
        map.addLayer(
            {
                id: `${MapConstants.id.layer.geojsonPreview.line}-segment-${surfaceType.id}`,
                type: 'line',
                source: MapConstants.id.source.geojsonPreview,
                paint: {
                    'line-color': surfaceType.style.color,
                    'line-width': MapConstants.style.line.width.dataLineString,
                    'line-dasharray': surfaceType.style.dasharray,
                },
                layout: {
                    visibility: 'visible',
                },
                filter: [
                    'all',
                    ['==', '$type', 'LineString'],
                    ['has', 'surfaceType'],
                    ['!has', 'isHome'],
                    ['==', 'surfaceType', surfaceType.id],
                ],
            },
            MapConstants.placeHolderLayerId,
        );
    });
    // Add geojson lines
    map.addLayer(
        {
            id: `${MapConstants.id.layer.geojsonPreview.symbol}-circle`,
            type: 'circle',
            source: MapConstants.id.source.geojsonPreview,
            paint: {
                'circle-radius': 10,
                'circle-color': MapConstants.style.linePreview,
            },
            layout: {
                visibility: 'visible',
            },
            filter: ['all', ['!has', 'isHome'], ['==', '$type', 'Point']],
        },
        MapConstants.placeHolderLayerId,
    );
    map.addLayer(
        {
            id: `${MapConstants.id.layer.geojsonPreview.symbol}-label`,
            type: 'symbol',
            source: MapConstants.id.source.geojsonPreview,
            paint: {
                'text-color': MapConstants.style.labelDark,
                'text-halo-color': MapConstants.style.labelHalo,
                'text-halo-width': 2,
                'text-halo-blur': 1,
            },
            layout: {
                'icon-image': '{iconMarker}',
                'text-anchor': 'top',
                'text-offset': [0, 1],
                'text-field': [
                    'format',
                    // ['get', 'id'],
                    // { 'font-scale': 1.2 },
                    // '\n',
                    // {},
                    ['get', 'name'],
                    // { 'font-scale': 0.8 },
                    { 'font-scale': 1 },
                ],
                'text-size': 11,
                visibility: 'visible',
            },
            filter: ['all', ['!has', 'isHome'], ['==', '$type', 'Point']],
        },
        MapConstants.placeHolderLayerId,
    );
    map.addLayer({
        id: `${MapConstants.id.layer.geojsonPreview.symbol}-home-lines`,
        type: 'line',
        source: MapConstants.id.source.geojsonPreview,
        paint: {
            'line-color': '#C00',
            'line-width': 2,
        },
        layout: {
            visibility: 'visible',
        },
        filter: ['all', ['==', '$type', 'LineString'], ['has', 'isHome']],
    });
    map.addLayer({
        id: `${MapConstants.id.layer.geojsonPreview.symbol}-home-points`,
        type: 'symbol',
        source: MapConstants.id.source.geojsonPreview,
        paint: {
            'text-color': MapConstants.style.labelDark,
            'text-halo-color': MapConstants.style.labelHalo,
            'text-halo-width': 2,
            'text-halo-blur': 1,
            'icon-halo-color': '#000',
            'icon-halo-width': 2,
            'icon-halo-blur': 1,
        },
        layout: {
            'icon-image': 'marker-bike-cyan',
            'icon-allow-overlap': true,
            'icon-anchor': 'bottom',
            'text-anchor': 'top',
            'text-offset': [0, 1],
            'text-field': ['format', ['get', 'name'], { 'font-scale': 1 }],
            'text-size': 11,
            visibility: 'visible',
        },
        filter: ['all', ['has', 'isHome'], ['==', '$type', 'Point']],
    });
    map.addLayer({
        id: `${MapConstants.id.layer.currentPositionMarker}`,
        type: 'circle',
        source: MapConstants.id.source.currentPositionMarker,
        paint: {
            'circle-radius': 6,
            'circle-color': MapConstants.style.labelDark,
        },
        layout: {
            visibility: 'visible',
        },
        filter: ['==', '$type', 'Point'],
    });
    // map.addLayer({
    //     id: 'sky',
    //     type: 'sky',
    //     paint: {
    //         // set up the sky layer to use a color gradient
    //         'sky-type': 'gradient',
    //         // the sky will be lightest in the center and get darker moving radially outward
    //         // this simulates the look of the sun just below the horizon
    //         'sky-gradient': [
    //             'interpolate',
    //             ['linear'],
    //             ['sky-radial-progress'],
    //             0.8,
    //             'rgba(135, 206, 235, 1.0)',
    //             1,
    //             'rgba(0,0,0,0.1)',
    //         ],
    //         'sky-gradient-center': [0, 0],
    //         'sky-gradient-radius': 90,
    //         'sky-opacity': [
    //             'interpolate',
    //             ['exponential', 0.1],
    //             ['zoom'],
    //             5,
    //             0,
    //             22,
    //             1,
    //         ],
    //     },
    // });
};

const addAdditionalLayers = (map, sky = false) => {
    // Add RBG terrain layer
    // map.addLayer({
    //     id: MapConstants.id.layer.rgbTerrain,
    //     type: 'raster',
    //     source: MapConstants.id.source.rgbTerrain,
    //     layout: {
    //         visibility: 'none',
    //     },
    // });
    if (sky) {
        map.addLayer({
            id: 'sky',
            type: 'sky',
            paint: {
                // set up the sky layer to use a color gradient
                'sky-type': 'gradient',
                // the sky will be lightest in the center and get darker moving radially outward
                // this simulates the look of the sun just below the horizon
                'sky-gradient': [
                    'interpolate',
                    ['linear'],
                    ['sky-radial-progress'],
                    0.8,
                    'rgba(135, 206, 235, 1.0)',
                    1,
                    'rgba(0,0,0,0.1)',
                ],
                'sky-gradient-center': [0, 0],
                'sky-gradient-radius': 90,
                'sky-opacity': [
                    'interpolate',
                    ['exponential', 0.1],
                    ['zoom'],
                    5,
                    0,
                    22,
                    1,
                ],
            },
        });
    }
};

const addDrawRoutesLayers = (map) => {
    map.addLayer(
        {
            id: `${MapConstants.id.layer.geojsonRoutePreview.line}-blue-outline`,
            type: 'line',
            source: MapConstants.id.source.geojsonRoutePreview,
            paint: {
                'line-color': MapConstants.style.drawLinePreview,
                'line-opacity': 0.8,
                'line-width': 6,
            },
            layout: {
                visibility: 'visible',
            },
            filter: ['all', ['has', 'isDraw'], ['==', '$type', 'LineString']],
        },
        MapConstants.placeHolderLayerId,
    );
    map.addLayer(
        {
            id: `${MapConstants.id.layer.geojsonRoutePreview.line}-white-underline`,
            type: 'line',
            source: MapConstants.id.source.geojsonRoutePreview,
            paint: {
                'line-color': '#ffffff',
                'line-opacity': 1,
                'line-width': 2,
            },
            layout: {
                visibility: 'visible',
            },
            filter: ['all', ['has', 'isDraw'], ['==', '$type', 'LineString']],
        },
        MapConstants.placeHolderLayerId,
    );
    map.addLayer(
        {
            id: `${MapConstants.id.layer.geojsonRoutePreview.symbol}-circle`,
            type: 'circle',
            source: MapConstants.id.source.geojsonRoutePreview,
            paint: {
                'circle-radius': 10,
                'circle-color': MapConstants.style.drawLinePreview,
            },
            layout: {
                visibility: 'visible',
            },
            filter: ['all', ['has', 'isDraw'], ['==', '$type', 'Point']],
        },
        MapConstants.placeHolderLayerId,
    );
};

const addFocusLayers = (map) => {
    // Focus lines, probably home
    // Outline
    map.addLayer(
        {
            id: `${MapConstants.id.layer.geojsonFocus.line}-outline`,
            type: 'line',
            source: MapConstants.id.source.geojsonFocus,
            paint: {
                'line-color': MapConstants.style.focusOutline,
                'line-opacity': 0.5,
                'line-width': 16,
            },
            layout: {
                visibility: 'visible',
            },
            filter: ['all', ['==', '$type', 'LineString']],
        },
        MapConstants.placeHolderLayerId,
    );
    map.addLayer(
        {
            id: `${MapConstants.id.layer.geojsonFocus.symbol}-circle-outline`,
            type: 'circle',
            source: MapConstants.id.source.geojsonFocus,
            paint: {
                'circle-radius': MapConstants.style.circle.radius.regularFocus,
                'circle-opacity': 0.5,
                'circle-color': MapConstants.style.focusOutline,
            },
            layout: {
                visibility: 'visible',
            },
            filter: ['all', ['==', '$type', 'Point']],
        },
        MapConstants.placeHolderLayerId,
    );
};
